import { useState } from "react";
import { toast } from "sonner";
import { DownloadIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useCambiarFormato, useEmitir } from "@/hooks/useEmision";
import { descargarArchivo, getApiErrorMessage } from "@/lib/api";
import {
  TITULO_DEL_BOTON_BLOQUEADO,
  etiquetaDeVersion,
  filasDelChecklist,
  leerErrorDeEmision,
  metaDeLaVersion,
  rutaDelPdfEmitido,
  textoDelBotonDeEmitir,
  type CondicionDeEmision,
  type CondicionesDeEmision,
  type DestinoDeLaCondicion,
  type ElementoDeLaFila,
  type Emision,
  type FilaDelChecklist,
  type TonoDeLaFila,
} from "@/lib/emision";
import type { FormatoDelCatalogo } from "@/lib/informes";
import { cn } from "@/lib/utils";

interface PanelDeEmisionProps {
  informeId: number;
  /** Los formatos que admite la plantilla del informe. */
  formatos: FormatoDelCatalogo[];
  formatoActualId: number | null;
  condiciones: CondicionesDeEmision | undefined;
  cargandoCondiciones: boolean;
  emisiones: Emision[];
  onIr: (destino: DestinoDeLaCondicion) => void;
}

const FONDO_DE_LA_FILA: Record<TonoDeLaFila, string> = {
  ok: "bg-superficie-tenue",
  aviso: "bg-superficie-tenue",
  bloquea: "bg-danger-soft",
};

const COLOR_DEL_ICONO: Record<TonoDeLaFila, string> = {
  ok: "bg-success-dot",
  aviso: "bg-warning-dot",
  bloquea: "bg-danger-dot",
};

/**
 * La columna izquierda de la fase 4: formato de salida, «Antes de emitir», el
 * botón de emitir y las versiones ya emitidas.
 *
 * Nada de lo que bloquea se decide acá: las filas salen de las condiciones del
 * servidor y cada una lleva a la fase que la resuelve.
 */
export function PanelDeEmision({
  informeId,
  formatos,
  formatoActualId,
  condiciones,
  cargandoCondiciones,
  emisiones,
  onIr,
}: PanelDeEmisionProps) {
  const cambiarFormato = useCambiarFormato(informeId);
  const emitir = useEmitir(informeId);
  const [bloqueos, setBloqueos] = useState<CondicionDeEmision[] | null>(null);
  const [errorDeEmision, setErrorDeEmision] = useState<string | null>(null);

  const puedeEmitir = condiciones?.puede_emitir === true;

  function alCambiarElFormato(valor: string) {
    cambiarFormato.mutate(Number(valor), {
      onSuccess: () => toast.success("Formato de salida guardado"),
      onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos cambiar el formato. Inténtalo de nuevo.")),
    });
  }

  function alEmitir() {
    setBloqueos(null);
    setErrorDeEmision(null);

    emitir.mutate(undefined, {
      onSuccess: (emision) =>
        toast.success(`Informe emitido ${etiquetaDeVersion(emision.version)} · documento congelado`),
      onError: (error) => {
        const lectura = leerErrorDeEmision(error);
        if (lectura.tipo === "bloqueada") setBloqueos(lectura.bloqueos);
        else setErrorDeEmision(lectura.mensaje);
      },
    });
  }

  return (
    <section aria-label="Emisión" className="bg-card flex flex-col gap-4 rounded-[1.25rem] border-[1.5px] p-5">
      <div className="space-y-1.5">
        <label htmlFor="formato-de-salida" className="text-xs font-bold">
          Formato de salida
        </label>
        <NativeSelect
          id="formato-de-salida"
          className="h-11 w-full rounded-xl text-[0.84375rem] font-semibold"
          value={formatoActualId ?? ""}
          disabled={cambiarFormato.isPending || formatos.length === 0}
          onChange={(evento) => alCambiarElFormato(evento.target.value)}
        >
          {formatoActualId === null ? <NativeSelectOption value="">Elige un formato</NativeSelectOption> : null}
          {formatos.map((formato) => (
            <NativeSelectOption key={formato.id} value={formato.id}>
              {formato.nombre}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>

      <div className="space-y-2">
        <h3 className="text-xs font-bold">Antes de emitir</h3>
        {cargandoCondiciones || condiciones === undefined ? (
          <Skeleton className="h-24 w-full rounded-xl" />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {filasDelChecklist(condiciones).map((fila) => (
              <li key={fila.clave}>
                <FilaDeLaLista fila={fila} onIr={onIr} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <Button
        type="button"
        variant="fuerte"
        className="h-[2.875rem] w-full rounded-xl text-[0.84375rem] font-extrabold"
        disabled={!puedeEmitir || emitir.isPending}
        title={puedeEmitir ? undefined : TITULO_DEL_BOTON_BLOQUEADO}
        onClick={alEmitir}
      >
        {emitir.isPending ? "Emitiendo…" : textoDelBotonDeEmitir(emisiones)}
      </Button>

      {emitir.isPending ? (
        <p role="status" className="text-ink-muted text-xs font-semibold">
          Estamos armando el documento final. Puede tardar unos segundos.
        </p>
      ) : null}

      {bloqueos ? (
        <div role="alert" className="bg-danger-soft text-danger-texto-fuerte rounded-xl px-3.5 py-3">
          <p className="text-[0.8125rem] font-bold">Todavía no se puede emitir</p>
          <ul className="mt-1 list-disc space-y-1 pl-4 text-xs font-semibold">
            {bloqueos.map((b) => (
              <li key={b.codigo}>{b.mensaje}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {errorDeEmision ? (
        <div role="alert" className="bg-danger-soft text-danger-texto-fuerte rounded-xl px-3.5 py-3">
          <p className="text-[0.8125rem] font-bold">No se pudo emitir</p>
          <p className="mt-1 text-xs font-semibold">{errorDeEmision}</p>
        </div>
      ) : null}

      <p className="text-ink-muted text-xs leading-normal font-semibold text-pretty">
        Un documento emitido se congela. Si después cambia algo, se emite una versión nueva y las anteriores se
        conservan.
      </p>

      {emisiones.length > 0 ? (
        <div className="border-divisor-fuerte border-t-[1.5px] pt-3">
          <h3 className="mb-2 text-xs font-bold">Versiones emitidas</h3>
          <ul className="flex flex-col gap-1.5">
            {emisiones.map((emision) => (
              <FilaDeVersion key={emision.version} informeId={informeId} emision={emision} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

/** Cuántos elementos se ven antes de pedir el resto: con decenas, la lista taparía el botón de emitir. */
const ELEMENTOS_VISIBLES = 5;

function FilaDeLaLista({ fila, onIr }: { fila: FilaDelChecklist; onIr: (destino: DestinoDeLaCondicion) => void }) {
  const [todos, setTodos] = useState(false);
  const bloquea = fila.tono === "bloquea";

  const encabezado = (
    <>
      <span
        aria-hidden
        className={cn(
          "text-background mt-0.5 flex size-[1.125rem] shrink-0 items-center justify-center rounded-full text-[0.6875rem] font-extrabold",
          COLOR_DEL_ICONO[fila.tono],
        )}
      >
        {fila.icono}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-ink text-[0.78125rem] font-bold">
          {fila.titulo}
          {bloquea && fila.cantidad > 1 ? ` (${fila.cantidad})` : ""}
        </span>
        <span className={cn("text-[0.71875rem] font-semibold", bloquea ? "text-danger-texto" : "text-ink-muted")}>
          {fila.detalle}
        </span>
      </span>
    </>
  );

  const clasesDelEncabezado = "flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left";
  const destino = fila.destino;
  const visibles = todos ? fila.elementos : fila.elementos.slice(0, ELEMENTOS_VISIBLES);
  const ocultos = fila.elementos.length - ELEMENTOS_VISIBLES;

  return (
    <div className={cn("rounded-xl", FONDO_DE_LA_FILA[fila.tono])}>
      {destino === null ? (
        <div className={clasesDelEncabezado}>{encabezado}</div>
      ) : (
        <button
          type="button"
          className={cn(clasesDelEncabezado, "hover:ring-border cursor-pointer hover:ring-[1.5px]")}
          onClick={() => onIr(destino)}
        >
          {encabezado}
        </button>
      )}

      {fila.elementos.length > 0 ? (
        <ul className="flex flex-col gap-0.5 pr-3 pb-2.5 pl-[2.375rem]">
          {visibles.map((elemento) => (
            <li key={elemento.clave}>
              <ElementoDeLaLista elemento={elemento} bloquea={bloquea} onIr={onIr} />
            </li>
          ))}
          {ocultos > 0 ? (
            <li>
              <button
                type="button"
                className={cn(
                  "block pt-0.5 text-[0.71875rem] font-bold hover:underline",
                  bloquea ? "text-danger-texto-fuerte" : "text-ink-secundario",
                )}
                onClick={() => setTodos((actual) => !actual)}
              >
                {todos ? "Ver menos" : `Ver los ${fila.elementos.length}`}
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

function ElementoDeLaLista({
  elemento,
  bloquea,
  onIr,
}: {
  elemento: ElementoDeLaFila;
  bloquea: boolean;
  onIr: (destino: DestinoDeLaCondicion) => void;
}) {
  const texto = (
    <>
      <span className={cn("font-bold", bloquea ? "text-danger-texto-fuerte" : "text-ink-secundario")}>
        {elemento.etiqueta}
      </span>
      {elemento.motivo ? (
        <span className={bloquea ? "text-danger-texto" : "text-ink-muted"}> · {elemento.motivo}</span>
      ) : null}
    </>
  );
  const clases = "block text-left text-[0.71875rem] leading-snug font-semibold";
  const destino = elemento.destino;

  if (destino === null) return <span className={clases}>{texto}</span>;

  return (
    <button type="button" className={cn(clases, "cursor-pointer hover:underline")} onClick={() => onIr(destino)}>
      {texto}
    </button>
  );
}

function FilaDeVersion({ informeId, emision }: { informeId: number; emision: Emision }) {
  const [descargando, setDescargando] = useState(false);

  async function descargar() {
    setDescargando(true);
    try {
      await descargarArchivo(rutaDelPdfEmitido(informeId, emision.version), `informe-${etiquetaDeVersion(emision.version)}.pdf`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No pudimos descargar el PDF de esta versión."));
    } finally {
      setDescargando(false);
    }
  }

  return (
    <li className="text-ink-secundario flex items-center gap-2 text-xs font-semibold">
      <span className="text-ink font-mono font-bold">{etiquetaDeVersion(emision.version)}</span>
      <span className="min-w-0 flex-1 truncate">{metaDeLaVersion(emision)}</span>
      <button
        type="button"
        disabled={descargando}
        aria-label={`Descargar el PDF de la versión ${etiquetaDeVersion(emision.version)}`}
        onClick={() => void descargar()}
        className="text-success-soft-foreground inline-flex items-center gap-1 text-xs font-bold hover:underline disabled:opacity-50"
      >
        <DownloadIcon aria-hidden className="size-3.5" />
        PDF
      </button>
    </li>
  );
}
